import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, ShieldX, RefreshCw, Loader2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STATUS_MAP = {
  ok: { c: "#A6FF00", bg: "rgba(166,255,0,0.08)", bd: "rgba(166,255,0,0.35)", Icon: ShieldCheck, label: "Pass" },
  warn: { c: "#F7B500", bg: "rgba(247,181,0,0.08)", bd: "rgba(247,181,0,0.35)", Icon: ShieldAlert, label: "Warning" },
  missing: { c: "#EF4444", bg: "rgba(239,68,68,0.08)", bd: "rgba(239,68,68,0.35)", Icon: ShieldX, label: "Missing" },
};

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.missing;
  const I = s.Icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 pg-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
      style={{ background: s.bg, border: `1px solid ${s.bd}`, color: s.c }}
    >
      <I size={11} /> {s.label}
    </span>
  );
}

function OverallBanner({ score, overall }) {
  const colour =
    overall === "good" ? "#A6FF00"
    : overall === "needs_attention" ? "#F7B500"
    : "#EF4444";
  const headline =
    overall === "good"
      ? "Mail DNS is healthy — emails will land in the inbox"
      : overall === "needs_attention"
        ? "DNS partially set — emails may still go to junk"
        : "DNS missing — emails are almost certainly going to junk";
  return (
    <div
      className="pg-card p-5 mb-6"
      style={{ borderColor: `${colour}55` }}
      data-testid="deliverability-overall"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="pg-micro mb-1">// Overall score</div>
          <div
            className="text-2xl pg-heading"
            style={{ fontFamily: "'Rajdhani'", color: colour }}
          >
            {score}% healthy
          </div>
          <div className="text-[13px] text-[#F7FAFC] mt-1">{headline}</div>
        </div>
        <div className="max-w-[420px] text-[12px] text-[#9CA8B8] leading-relaxed">
          We check SPF, DKIM and DMARC live against your domain's public DNS every time you open
          this page. Any "Missing" record means mailbox providers (Gmail, Outlook, Yahoo) lack
          proof that Resend is authorised to send on your behalf — which pushes those emails to
          junk.
        </div>
      </div>
    </div>
  );
}

function Copyable({ text }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <div className="flex items-start gap-2 p-3 rounded-sm bg-[#0B1220] border border-[#1E2E44]">
      <pre
        className="flex-1 text-[12px] text-[#5EEAD4] whitespace-pre-wrap break-all font-normal leading-relaxed"
        style={{ fontFamily: "inherit" }}
      >
{text}
      </pre>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
            toast.success("Copied");
          } catch {
            toast.error("Could not copy");
          }
        }}
        className="pg-btn-ghost text-[10px] uppercase tracking-wider"
      >
        {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export default function AdminDeliverability() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/deliverability");
      setData(data);
    } catch {
      toast.error("Could not load deliverability check");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-deliverability-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="pg-micro mb-2">// Admin · Email deliverability</div>
          <h1
            className="text-[#F7FAFC] text-3xl pg-display"
            style={{ fontFamily: "'Exo 2'" }}
          >
            Inbox vs junk
          </h1>
          <p className="text-[#9CA8B8] text-sm mt-1 max-w-2xl">
            Live DNS check for <strong className="text-[#F7FAFC]">{data?.domain || "peptigenlabs.co.uk"}</strong>.
            Set SPF, DKIM and DMARC to green and your transactional + campaign emails will land in
            the inbox instead of junk.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="pg-btn-ghost text-xs"
          data-testid="deliverability-refresh"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Re-check DNS
        </button>
      </div>

      {loading && !data ? (
        <div className="pg-card p-10 text-center text-[#9CA8B8] text-sm">
          <Loader2 size={18} className="animate-spin text-[#00C2D6] mx-auto mb-3" /> Querying DNS…
        </div>
      ) : data ? (
        <>
          <OverallBanner score={data.score_pct} overall={data.overall} />
          <div className="space-y-4">
            {data.checks.map((c) => (
              <div
                key={c.key}
                className="pg-card p-5"
                data-testid={`deliverability-check-${c.key}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <div className="pg-micro mb-1">// {c.label}</div>
                    <div
                      className="text-lg pg-heading text-[#F7FAFC]"
                      style={{ fontFamily: "'Rajdhani'" }}
                    >
                      {c.key.toUpperCase()} record
                    </div>
                  </div>
                  <StatusPill status={c.status} />
                </div>
                {c.value && (
                  <div className="mb-3">
                    <div className="pg-micro mb-1.5">// Currently published</div>
                    <Copyable text={c.value} />
                  </div>
                )}
                {c.hint && (
                  <div>
                    <div className="pg-micro mb-1.5" style={{ color: "#F7B500" }}>
                      // What to add
                    </div>
                    <Copyable text={c.hint} />
                  </div>
                )}
                {!c.hint && c.status === "ok" && (
                  <div className="text-[12px] text-[#A6FF00] leading-relaxed">
                    Record looks good. No action needed.
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="pg-card p-5 mt-6" data-testid="deliverability-tips">
            <div className="pg-micro mb-2">// Beyond DNS — content & reputation</div>
            <ul className="text-[13px] text-[#9CA8B8] space-y-2 leading-relaxed list-disc pl-5">
              <li>
                Every email now includes a <strong className="text-[#F7FAFC]">plain-text alternative</strong> alongside the HTML
                — large win vs html-only mail, already applied.
              </li>
              <li>
                <strong className="text-[#F7FAFC]">Reply-To is set to info@peptigenlabs.co.uk</strong> so replies flow to the
                brand inbox, signalling a conversational sender.
              </li>
              <li>
                Marketing campaigns carry the <strong className="text-[#F7FAFC]">List-Unsubscribe</strong> header (RFC 8058),
                mandatory for Gmail / Yahoo bulk-sender rules.
              </li>
              <li>
                Keep sending from <strong className="text-[#F7FAFC]">info@peptigenlabs.co.uk</strong> only. Mixing
                domains (e.g. noreply@resend.dev) damages deliverability.
              </li>
              <li>
                We tell users to check Spam/Junk at every capture point, and to mark us "Not junk" —
                over time the mailbox provider learns we belong in the inbox.
              </li>
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}
